const { communicationText, resultCommunication } = require('../utils/pabsCommunication');

function safeObject(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function governedTaskStatus(record = {}) {
  const result = safeObject(record.result || record);
  const capability = record.capability || record.playbook?.capability;
  const hasHumanDisposition = Boolean(
    record.design_review_decision_id ||
    record.calibration?.id ||
    record.calibration?.human_disposition ||
    result.continuation?.disposition ||
    result.continuation?.corrected_artifact_accepted
  );

  // Completing Pabs's analysis does not close a governed Design Review. The
  // review remains actionable until the accountable human records a
  // disposition. A superseded review is historical and must stay terminal.
  if (
    capability === "review_design_promotion" &&
    record.status === "completed" &&
    !hasHumanDisposition &&
    !record.superseded_by
  ) {
    return "waiting_for_user";
  }

  return record.status || null;
}

function titleFromMarkdown(value = "") {
  return String(value).match(/^#\s+(.+)$/m)?.[1]?.trim() || null;
}

function presentationText(value, preferredKeys = []) {
  if (value == null || value === "") return null;
  if (["string", "number", "boolean"].includes(typeof value)) return String(value);
  if (Array.isArray(value)) {
    const values = value
      .map(item => presentationText(item, preferredKeys))
      .filter(Boolean);
    return values.length ? values.join(", ") : null;
  }
  if (typeof value === "object") {
    const keys = [
      ...preferredKeys,
      "title",
      "summary",
      "message",
      "label",
      "name",
      "recommendation",
      "decision",
      "value",
    ];
    for (const key of [...new Set(keys)]) {
      const normalized = presentationText(value[key], preferredKeys);
      if (normalized) return normalized;
    }
  }
  return null;
}

function readableOutcome(value) {
  const text = presentationText(value);
  if (!text) return null;
  if (!/^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(text)) return text;
  const words = text.replace(/_/g, " ");
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;
}

function markdownSection(message, heading) {
  const escaped = String(heading).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = String(message || "").match(new RegExp(
    `(?:^|\\n)\\*\\*${escaped}:?\\*\\*\\s*([\\s\\S]*?)(?=\\n\\s*\\*\\*[^\\n]+:?\\*\\*|$)`,
    "i"
  ));
  return match?.[1]?.trim() || null;
}

function legacyMissionFindings(message) {
  const section = markdownSection(message, "What I found");
  if (!section) return [];
  return section.split(/\n+/)
    .map(line => line.trim().replace(/^[-*•]\s+/, ""))
    .filter((line, index, lines) => line && (index > 0 || /^[-*•]\s+/.test(section.trim())))
    .filter(Boolean);
}

function humanizeIdentifier(value = "") {
  const fieldLabels = {
    finalProgramSequence: "Action Plan",
    objectivesStatement: "Objectives",
    targetStatement: "Target Audience",
    problemStatement: "Problem Statement",
    goalStatement: "Goal",
    shortSummary: "Plan Summary",
  };
  if (fieldLabels[value]) return fieldLabels[value];
  const words = String(value)
    .replace(/^update_/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
  return words.replace(/\b\w/g, character => character.toUpperCase());
}

function parseStoredValue(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || !/^[\[{]/.test(trimmed)) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function planChangeValue({ capability, result, parameters }) {
  if (result.rawValue != null) return parseStoredValue(result.rawValue);

  const fieldName = String(capability || "").replace(/^update_/, "");
  const parameterCandidates = [
    fieldName,
    result.reactField,
    String(result.reactField || "").replace(/([A-Z])/g, "_$1").toLowerCase(),
  ].filter(Boolean);
  for (const key of parameterCandidates) {
    if (parameters[key] != null) return parseStoredValue(parameters[key]);
  }

  return parseStoredValue(result.value ?? result.summary ?? null);
}

function integrityWarnings(task, runSteps = []) {
  const result = safeObject(task?.result);
  const warnings = [];
  if (task?.status === "completed" && String(result.needs_user || "").trim()) {
    warnings.push({
      code: "completed_with_open_request",
      severity: "error",
      message: "This legacy completion record still contains an unresolved user request.",
    });
  }
  if (task?.status === "completed") {
    const unsuccessfulTools = runSteps.filter(step =>
      step.type === "tool" && ["failed", "cancelled", "skipped", "pending", "running"].includes(step.status)
    );
    if (unsuccessfulTools.length) {
      warnings.push({
        code: "completed_with_unverified_steps",
        severity: "error",
        message: "This legacy completion record includes tool work without verified completion.",
        step_ids: unsuccessfulTools.map(step => step.id),
      });
    }
  }
  return warnings;
}

function presentationState(status) {
  if (status === "suggested") return "proposal";
  if (["todo", "pending", "approved", "paused", "waiting_for_trigger"].includes(status)) return "scheduled";
  if (status === "running") return "running";
  if (["awaiting_approval", "waiting_for_user", "waiting_for_approval", "waiting_for_evidence"].includes(status)) {
    return "needs_action";
  }
  if (status === "waiting_for_credits") return "waiting_for_credits";
  if (status === "blocked") return "blocked";
  if (status === "completed") return "completed";
  if (status === "failed") return "failed";
  if (["cancelled", "archived"].includes(status)) return "cancelled";
  return "scheduled";
}

function actionReason(status) {
  if (["awaiting_approval", "waiting_for_approval"].includes(status)) return "approval";
  if (status === "waiting_for_user") return "input";
  if (status === "waiting_for_evidence") return "evidence";
  if (status === "blocked") return "blocker";
  return null;
}

function workStatus(taskStatus, fallback = null) {
  if (fallback) return fallback;
  if (["waiting_for_user", "waiting_for_approval", "awaiting_approval"].includes(taskStatus)) return "awaiting_review";
  if (taskStatus === "waiting_for_evidence") return "awaiting_evidence";
  if (taskStatus === "completed") return "final";
  if (taskStatus === "failed") return "preserved_after_failure";
  if (taskStatus === "cancelled") return "cancelled";
  return "in_progress";
}

function linkedWorkStatus(record = {}) {
  const status = governedTaskStatus(record);
  const capability = record.capability || record.task_type;
  const result = safeObject(record.result || record);
  const parameters = safeObject(record.parameters);

  if (capability === 'external_blocker_work') {
    if (result.state === 'ready_for_ai_tool' && status === 'waiting_for_evidence') return 'Ready for AI tool';
    if (status === 'running') return 'In progress with AI tool';
    if (status === 'completed') return 'Output returned · blocker assessment separate';
  }

  if (record.superseded_by) return "Replaced by a newer review";
  if (capability === "generate_design_implementation_comparison" && status === "waiting_for_evidence") {
    return result.implementation?.version || parameters.implementation_version
      ? "Waiting for evidence" : "Waiting for delivery";
  }

  if (capability === "review_design_promotion" && status === "waiting_for_evidence") {
    const request = safeObject(safeObject(result.review).evidence_request);
    return request.human_intervention_required === true || request.audience === "human"
      ? "Needs your help"
      : "Waiting for AI builder";
  }
  if (capability === "generate_design_remediation_plan") {
    if (status === "completed") return "Corrections returned";
    if (status === "waiting_for_evidence" && result.handoff_confirmed_at) return "Ready for builder";
    if (["waiting_for_user", "waiting_for_approval", "awaiting_approval"].includes(status)) return "Confirm corrections";
  }
  if (capability === "product_requirement_issue") {
    if (parameters.source_type === "design_review_candidate" && status === "waiting_for_user") return "Needs product decision";
    if (status === "waiting_for_user") return "Needs review";
    if (status === "waiting_for_evidence") {
      if (parameters.routing_target === "human_review") return "Needs review";
      if (parameters.routing_target === "human_implementation") return "Ready for human";
      return "Ready for AI builder";
    }
    if (["todo", "pending"].includes(status)) return "Ready for handoff";
    if (status === "waiting_for_approval") return "Delivery review";
  }
  return ({
    suggested: "Suggested", todo: "Planned", pending: "Coming up soon", running: "In progress",
    waiting_for_user: "Needs your input", waiting_for_approval: "Needs review",
    awaiting_approval: "Needs review", waiting_for_evidence: "Waiting for evidence",
    waiting_for_credits: "Needs credits", blocked: "Blocked", completed: "Completed",
    failed: "Something went wrong", cancelled: "Cancelled", archived: "Archived",
  })[status] || String(status || "Unknown").replace(/_/g, " ");
}

// Status badges should describe lifecycle only. Workflow meaning such as
// "corrections returned" or "replaced by a newer review" belongs in the story
// beside the job, otherwise a historical relationship looks like a new status.
function lifecycleStatusLabel(record = {}) {
  const status = governedTaskStatus(record);
  if (record.capability === 'external_blocker_work' && status === 'waiting_for_evidence' && safeObject(record.result).state === 'ready_for_ai_tool') return 'Ready for AI tool';
  if (["suggested", "todo", "pending", "paused"].includes(status)) return "Planned";
  if (status === "running") return "In progress";
  if (["waiting_for_user", "waiting_for_approval", "awaiting_approval"].includes(status)) return "Needs input";
  if (["waiting_for_evidence", "waiting_for_credits", "blocked"].includes(status)) return "Waiting";
  if (status === "completed") return "Completed";
  if (status === "failed") return "Failed";
  if (["cancelled", "archived"].includes(status)) return "Cancelled";
  return status ? humanizeIdentifier(status) : "Unknown";
}

// Shared presentation of persisted communication. This does not execute work
// or infer success from an approval: the producer owns those facts.
function jobResponse(resultValue = {}) {
  const result = safeObject(resultValue);
  const continuation = safeObject(result.continuation);
  return {
    message: continuation.message || result.assistant_message || result.message || null,
    action: continuation.task_id ? {
      type: "open_job", task_id: continuation.task_id, label: "View next job",
    } : null,
    return: safeObject(result.work_protocol).return || safeObject(result.work_contract).return || null,
  };
}

function structuralIds(record = {}) {
  const parameters = safeObject(record.parameters);
  const result = safeObject(record.result);
  const protocol = safeObject(result.work_protocol || parameters.work_protocol);
  const related = safeObject(result.related);
  const args = safeObject(parameters.args);
  const scalarIds = [
    related.source_task_id,
    related.requirement_id,
    related.decision_task_id,
    args.source_task_id,
    args.requirement_id,
    args.decision_task_id,
    parameters.source_task_id,
    result.source_task_id,
    parameters.parent_task_id,
    result.parent_task_id,
    parameters.parent_decision_task_id,
    result.parent_decision_task_id,
    parameters.linked_decision_task_id,
    result.linked_decision_task_id,
    protocol.parent_work_id,
    protocol.root_work_id,
    safeObject(result.continuation).task_id,
  ];
  const collectionIds = [
    parameters.child_task_ids,
    result.child_task_ids,
    parameters.child_requirement_ids,
    result.child_requirement_ids,
    parameters.linked_task_ids,
    result.linked_task_ids,
    parameters.linked_requirement_ids,
    result.linked_requirement_ids,
  ].flatMap(value => Array.isArray(value) ? value : []);
  return [...new Set([...scalarIds, ...collectionIds].map(Number).filter(Number.isFinite))];
}

function projectLinkedWork(currentTask, records = [], { relatedTaskIds = [], includeCurrent = false } = {}) {
  if (!currentTask?.id) return [];
  const all = new Map([...records, currentTask].filter(Boolean).map(record => [Number(record.id), record]));
  const adjacency = new Map([...all.keys()].map(id => [id, new Set()]));
  const byReviewReference = new Map();
  const byRemediationReference = new Map();
  const supersededReviewReferences = new Set();

  for (const [id, record] of all) {
    const parameters = safeObject(record.parameters);
    const result = safeObject(record.result);
    const reviewReference = result.review_reference || parameters.review_reference;
    const remediationReference = result.remediation_reference || parameters.remediation_reference;
    const supersedesReviewReference = result.supersedes_review_reference || parameters.supersedes_review_reference;
    if (reviewReference) byReviewReference.set(String(reviewReference), id);
    if (remediationReference) byRemediationReference.set(String(remediationReference), id);
    if (supersedesReviewReference) supersededReviewReferences.add(String(supersedesReviewReference));
  }
  const connect = (left, right) => {
    left = Number(left); right = Number(right);
    if (!all.has(left) || !all.has(right) || left === right) return;
    adjacency.get(left).add(right);
    adjacency.get(right).add(left);
  };
  for (const [id, record] of all) {
    structuralIds(record).forEach(other => connect(id, other));
    const parameters = safeObject(record.parameters);
    const result = safeObject(record.result);
    const supersedes = result.supersedes_review_reference || parameters.supersedes_review_reference;
    const reviewReference = result.review_reference || parameters.review_reference;
    const remediationReference = result.remediation_reference || parameters.remediation_reference;
    if (supersedes) connect(id, byReviewReference.get(String(supersedes)));
    if (reviewReference) connect(id, byReviewReference.get(String(reviewReference)));
    if (remediationReference) connect(id, byRemediationReference.get(String(remediationReference)));
  }

  const visited = new Set([Number(currentTask.id), ...relatedTaskIds.map(Number)].filter(id => all.has(id)));
  const queue = [...visited];
  while (queue.length) {
    for (const next of adjacency.get(queue.shift()) || []) {
      if (visited.has(next)) continue;
      visited.add(next);
      queue.push(next);
    }
  }
  if (!includeCurrent) visited.delete(Number(currentTask.id));
  return [...visited]
    .map(id => all.get(id))
    .filter(record => record && !String(record.task_type || "").startsWith("MCP"))
    // Newest first: the reader wants the most recent linked job at the top, and a
    // job that has completed is placed by when it completed, not when it was created.
    .sort((a, b) => new Date(b.completed_at || b.created_at || 0) - new Date(a.completed_at || a.created_at || 0) || Number(b.id) - Number(a.id))
    .map(record => {
      const parameters = safeObject(record.parameters);
      const result = safeObject(record.result);
      const reviewReference = result.review_reference || parameters.review_reference;
      const wasFollowedByNewerReview = (record.capability || record.task_type) === "review_design_promotion"
        && reviewReference && supersededReviewReferences.has(String(reviewReference));
      const workName = parameters.work_name || result.work_name || safeObject(result.artifact).name;
      return {
        id: record.id,
        kind: (record.capability || record.task_type) === "product_requirement_issue" ? "issue" : "job",
        title: workName || record.task_type || record.capability || "Pabs job",
        capability: record.capability || null,
        status: record.status,
        status_label: lifecycleStatusLabel(wasFollowedByNewerReview ? { ...record, superseded_by: true } : record),
        relationship_label: wasFollowedByNewerReview
          ? "Followed by a newer review"
          : null,
        created_at: record.created_at || null,
        completed_at: record.completed_at || null,
      };
    });
}

// A routine owns a schedule; its linked entries are existing execution jobs and
// their relationships. Reuse the same graph as individual job details, scoped
// to the routine's plan, without copying run output into the schedule view.
function projectRoutineLinkedWork(routine = {}, records = []) {
  const planId = Number(routine.plan_id || routine._plan_id);
  if (!planId) return [];
  const parameters = safeObject(routine.parameters);
  const missionId = Number(routine.mission_id || parameters.mission_id || parameters.missionId || safeObject(parameters.mission).id);
  const recurrenceId = parameters.routine_id || parameters.schedule_id || parameters.recurrence_id;
  const isPulse = routine.task_type === 'generate_pulse' || routine.capability === 'generate_pulse';
  const scoped = records.filter(record => Number(record.plan_id || record._plan_id) === planId && record.status !== 'archived');
  const tasks = scoped.filter(record => !record.record_type || record.record_type === 'task');
  const executions = tasks.filter(record => {
    if (isPulse) return record.capability === 'generate_pulse' || record.task_type === 'generate_pulse';
    if (missionId) {
      const params = safeObject(record.parameters);
      return Number(record.mission_id || params.mission_id || params.missionId || safeObject(params.mission).id) === missionId;
    }
    if (recurrenceId) {
      const params = safeObject(record.parameters);
      return String(params.routine_id || params.schedule_id || params.recurrence_id || '') === String(recurrenceId);
    }
    return String(record.id) === String(routine.id);
  });
  const linked = executions.length ? projectLinkedWork(executions[0], tasks, {
    relatedTaskIds: executions.map(record => record.id), includeCurrent: true,
  }).map(record => ({ ...record, record_type: 'task' })) : [];
  const representedPulseIds = new Set(tasks.map(record => Number(safeObject(record.result).pulse_id)).filter(Boolean));
  const legacyPulses = isPulse ? scoped.filter(record => record.record_type === 'pulse' && !representedPulseIds.has(Number(record.id))).map(record => ({
    id: record.id, record_type: 'pulse', kind: 'job', title: record.subject || 'Strategy pulse',
    status: 'completed', status_label: 'Completed', created_at: record.created_at,
  })) : [];
  return [...linked, ...legacyPulses].sort((a, b) => new Date(b.completed_at || b.created_at || 0) - new Date(a.completed_at || a.created_at || 0) || Number(b.id) - Number(a.id));
}

function workAction(kind, id, status) {
  if (!id) return null;
  const definitions = {
    document: ["open_document", "Open document"],
    pulse: ["open_pulse", "Open strategy pulse"],
    requirement: ["open_requirement", status === "awaiting_review" ? "Review requirement" : "Open requirement"],
    github_change: ["open_github_change", status === "awaiting_review" ? "Review change" : "Open change"],
    email: ["view_email", status === "awaiting_review" ? "Review email" : "View email"],
    decision: ["review_decision", "Review decision"],
    connector_record: ["open_external", "Open in connected tool"],
  };
  const definition = definitions[kind];
  return definition ? { type: definition[0], label: definition[1], target: String(id) } : null;
}

function normalizeWorkItem(item) {
  if (!item?.kind) return null;
  const status = item.status || "created";
  const title = presentationText(item.title, ["title", "recommendation", "decision"]) || "Pabs output";
  return {
    id: String(item.id || `${item.kind}:${title}`),
    kind: item.kind,
    title,
    summary: presentationText(item.summary, ["summary", "call_reason", "rationale"]),
    communication: item.communication || resultCommunication(item.payload || {}),
    status,
    preview: presentationText(item.preview, ["preview", "content", "body"]),
    outcome: item.outcome || null,
    primary_action: item.primary_action || workAction(item.kind, item.target_id || item.id, status),
    secondary_actions: item.secondary_actions || [],
    delivery: item.delivery || null,
    evidence: item.evidence || [],
    relationships: item.relationships || {},
    run_id: item.run_id || null,
    created_at: item.created_at || null,
    payload: item.payload || {},
  };
}

function documentFromPayload(payload = {}, fallback = {}) {
  const artifacts = Array.isArray(payload.artifacts) ? payload.artifacts : [];
  const artifact = artifacts.find(item => item?.kind === "document") || {};
  const id = artifact.id || payload.document_id || fallback.id;
  if (!id) return null;
  return normalizeWorkItem({
    id: `document:${id}`,
    target_id: id,
    kind: "document",
    title: artifact.title || payload.title || fallback.title || "Generated document",
    summary: fallback.summary || payload.summary || null,
    preview: fallback.preview || payload.document_preview || null,
    status: fallback.status,
    run_id: fallback.run_id,
    created_at: payload.created_at || fallback.created_at,
    payload: { ...payload, ...artifact },
  });
}

function projectWork({ task, artifact, outputs = [], runSteps = [], interactions = [], runs = [] }) {
  const result = safeObject(task?.result);
  const parameters = safeObject(task?.parameters);
  const capability = task?.capability || task?.task_type || "";
  const status = task?.status;
  const items = [];
  const seen = new Set();
  const add = item => {
    const normalized = normalizeWorkItem(item);
    if (!normalized || seen.has(normalized.id)) return;
    seen.add(normalized.id);
    items.push(normalized);
  };
  const missionSourcesForRun = runId => [...new Map(runSteps
    .filter(step => step.status === "completed" && (!runId || step.run_id === runId))
    .map(step => {
      const output = safeObject(step.output);
      const id = output.connector_id || null;
      return id ? [id, { id, label: humanizeIdentifier(id), tool: step.tool_name || step.name || null }] : null;
    })
    .filter(Boolean)).values()];

  for (const output of outputs) {
    const payload = safeObject(output.payload);
    if (output.output_kind === "document") {
      add(documentFromPayload(payload, {
        id: output.artifact_id,
        title: output.title,
        summary: output.summary,
        status: workStatus(status, output.status === "created" ? null : output.status),
        run_id: output.run_id,
        created_at: output.created_at,
      }));
    } else if (output.output_kind === "requirement") {
      add({
        id: `requirement:${output.artifact_id || output.child_task_id || output.id}`,
        run_id: output.run_id,
        target_id: output.artifact_id || output.child_task_id,
        kind: "requirement",
        title: output.title || "Build requirement",
        summary: output.summary,
        status: workStatus(status, output.status),
        created_at: output.created_at,
        payload,
      });
    } else if (output.output_kind === "content_artifact" && (capability === 'agent_mission' || payload.communication || !outputs.some(item =>
      item.output_kind === "document" && Number(item.run_id || 0) === Number(output.run_id || 0)
    ))) {
      // task.result describes only the current/latest run. New outputs snapshot
      // their own widgets; the markdown adapter preserves pre-snapshot history.
      const openInteractionRunIds = new Set(interactions
        .filter(interaction => interaction.status === "open")
        .map(interaction => Number(interaction.run_id))
        .filter(Number.isFinite));
      const latestRun = [...runs].sort((left, right) =>
        new Date(right.updated_at || right.created_at || 0) - new Date(left.updated_at || left.created_at || 0)
        || Number(right.id || 0) - Number(left.id || 0)
      )[0] || null;
      const taskOwnsActiveResult = !["proposal", "scheduled"].includes(presentationState(task?.status));
      const ownsCurrentResult = task?.run_id
        ? (!output.run_id || Number(output.run_id) === Number(task.run_id))
        : openInteractionRunIds.has(Number(output.run_id))
          || (taskOwnsActiveResult && (!latestRun || Number(output.run_id) === Number(latestRun.id)));
      const findings = Array.isArray(payload.findings) && payload.findings.length
        ? payload.findings.filter(Boolean)
        : ownsCurrentResult && Array.isArray(result.material_findings)
          ? result.material_findings.filter(Boolean)
          : legacyMissionFindings(payload.message);
      const sources = Array.isArray(payload.sources) && payload.sources.length
        ? payload.sources
        : missionSourcesForRun(output.run_id);
      const needsUser = payload.needs_user
        || (ownsCurrentResult ? result.needs_user : null)
        || markdownSection(payload.message, "What I need from you");
      add({
        id: `text:${output.id}`,
        run_id: output.run_id,
        kind: capability === "agent_mission" || findings.length ? "mission_result" : "text_result",
        title: output.title || task?.task_type || "Pabs result",
        summary: communicationText(payload.communication, payload.message || output.summary),
        communication: payload.communication || null,
        status: workStatus(status, output.status === "final" ? "final" : null),
        created_at: output.created_at,
        payload: {
          ...payload,
          findings,
          sources,
          needs_user: needsUser || null,
          needs_user_response_type: payload.needs_user_response_type || (ownsCurrentResult ? result.needs_user_response_type : null),
          needs_user_options: payload.needs_user_options || (ownsCurrentResult ? result.needs_user_options : []),
          interaction_id: interactions.find(item => item.status === 'open' && Number(item.run_id) === Number(output.run_id))?.id || null,
          interaction_status: ownsCurrentResult ? "current" : "historical",
          next_trigger: payload.next_trigger
            || (ownsCurrentResult ? result.next_trigger : null)
            || markdownSection(payload.message, "I’ll reassess when"),
        },
      });
    }
  }

  for (const step of runSteps) {
    if (step.status !== "completed") continue;
    const payload = safeObject(step.output);
    if (step.tool_name === "generate_document") {
      add(documentFromPayload(payload, {
        title: step.name,
        status: workStatus(status),
        created_at: step.completed_at,
      }));
    }
  }

  if (capability === "prepare_design_brief" && Object.keys(safeObject(result.brief)).length) {
    const brief = safeObject(result.brief);
    add({
      id: `design_brief:${task.id}`,
      run_id: task.run_id,
      target_id: task.id,
      kind: "design_brief",
      title: brief.title || task.task_type || "Design brief",
      summary: brief.design_intent || task.reasoning,
      outcome: {
        type: result.assurance_level || "prepared",
        label: result.assurance_level
          ? `${humanizeIdentifier(result.assurance_level)} assurance`
          : "Brief prepared",
      },
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  } else if (capability === "review_design_promotion" && Object.keys(safeObject(result.review)).length) {
    const review = safeObject(result.review);
    add({
      id: `design_review:${task.id}`,
      run_id: task.run_id,
      target_id: task.id,
      kind: "design_authority_review",
      title: parameters.work_name || parameters.artifact_label || "Design review",
      summary: review.decision_summary || task.reasoning,
      outcome: {
        type: review.verdict || "reviewed",
        label: humanizeIdentifier(review.verdict || "review completed"),
      },
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  } else if (capability === "generate_design_remediation_plan" && Object.keys(safeObject(result.remediation)).length) {
    const remediation = safeObject(result.remediation);
    add({
      id: `design_remediation:${task.id}`,
      run_id: task.run_id,
      target_id: task.id,
      kind: "design_remediation",
      title: "Corrections before the next review",
      summary: remediation.summary || task.reasoning,
      outcome: {
        type: "remediation_ready",
        label: result.handoff_confirmed_at ? "Ready for the builder" : "Confirm corrections",
      },
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  } else if (capability === "generate_design_exception_review" && Object.keys(safeObject(result.assessment)).length) {
    const assessment = safeObject(result.assessment);
    add({
      id: `design_exception:${task.id}`,
      run_id: task.run_id,
      target_id: task.id,
      kind: "design_exception",
      title: parameters.standard_reference || "Design exception",
      summary: assessment.rationale || task.reasoning,
      outcome: { type: result.status || "pending_decision", label: humanizeIdentifier(result.disposition || assessment.recommendation || "review required") },
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  } else if (capability === "generate_accessibility_evidence_assessment" && Object.keys(safeObject(result.assessment)).length) {
    const assessment = safeObject(result.assessment);
    add({
      id: `accessibility_assessment:${task.id}`,
      run_id: task.run_id,
      target_id: task.id,
      kind: "accessibility_assessment",
      title: safeObject(result.artifact).name || parameters.work_name || "Accessibility evidence",
      summary: assessment.summary || task.reasoning,
      outcome: { type: assessment.status || "insufficient_evidence", label: humanizeIdentifier(assessment.status || "reviewed") },
      status: workStatus(status),
      created_at: task.completed_at,
      payload: { ...result, state: task.status || result.state },
    });
  } else if (capability === "generate_design_implementation_comparison" && Object.keys(safeObject(result.comparison)).length) {
    const comparison = safeObject(result.comparison);
    add({
      id: `design_comparison:${task.id}`,
      run_id: task.run_id,
      target_id: task.id,
      kind: "design_implementation_comparison",
      title: result.work_name || parameters.work_name || "Design and implementation",
      summary: comparison.summary || task.reasoning,
      outcome: { type: comparison.status || "insufficient_evidence", label: humanizeIdentifier(comparison.status || "reviewed") },
      status: workStatus(status), created_at: task.completed_at, payload: result,
    });
  } else if (capability === "generate_design_system_usage_review" && Object.keys(safeObject(result.system_usage_review)).length) {
    const review = safeObject(result.system_usage_review);
    add({ id: `design_system_usage:${task.id}`, run_id: task.run_id, target_id: task.id, kind: "design_system_usage_review", title: safeObject(result.artifact).name || parameters.work_name || "Design-system usage", summary: review.summary || task.reasoning, outcome: { type: review.status || "insufficient_evidence", label: humanizeIdentifier(review.status || "reviewed") }, status: workStatus(status), created_at: task.completed_at, payload: result });
  } else if (capability === "generate_document" || result.document_id || artifact?.type === "document") {
    add(documentFromPayload(result, {
      id: artifact?.data?.id,
      title: artifact?.data?.title || titleFromMarkdown(artifact?.data?.generated_markdown),
      preview: artifact?.data?.preview,
      status: workStatus(status),
      run_id: task.run_id,
      created_at: artifact?.data?.created_at,
    }));
  }

  for (const document of Array.isArray(result.artifacts) ? result.artifacts : []) {
    if (document?.kind === 'document') add(documentFromPayload({ artifacts: [document] }, { status: 'created', run_id: task.run_id }));
  }
  // A specialist report is the deliverable; findings remain supporting work.
  if (capability === 'generate_accessibility_evidence_assessment') {
    items.sort((a, b) => Number(b.kind === 'document') - Number(a.kind === 'document'));
  }

  if (["engagement_email", "send_user_email"].includes(capability) || result.recipient) {
    const emailId = result.resend_id || task?.id;
    add({
      id: `email:${emailId}`,
      target_id: task?.id,
      kind: "email",
      run_id: task.run_id,
      title: result.subject || parameters.subject || task?.task_type || "Email",
      summary: result.recipient ? `To ${result.recipient}` : parameters.recipient ? `To ${parameters.recipient}` : null,
      preview: result.body || parameters.body || null,
      status: result.sent_at ? "delivered" : workStatus(status),
      delivery: result.sent_at ? {
        channel: "email",
        recipient: result.recipient,
        delivered_at: result.sent_at,
      } : null,
      created_at: result.sent_at || task?.completed_at,
      payload: result,
    });
  } else if ((capability === "generate_pulse" && task?.status === "completed") || result.pulse_id || artifact?.type === "pulse") {
    const pulseId = result.pulse_id || artifact?.data?.id || task?.id;
    add({
      id: `pulse:${pulseId}`,
      target_id: pulseId,
      kind: "pulse",
      run_id: task.run_id,
      title: result.subject || "Strategy pulse",
      summary: null,
      preview: artifact?.data?.content || null,
      outcome: result.is_nudge
        ? { type: "no_change_required", label: "No plan change required" }
        : { type: "review_complete", label: "Strategy review complete" },
      status: workStatus(status),
      delivery: result.sent ? { channel: "email", status: "delivered" } : null,
      created_at: artifact?.data?.created_at || task?.completed_at,
      payload: result,
    });
  } else if (capability === "decision_capture") {
    const candidate = safeObject(result.decision_candidate);
    add({
      id: `decision:${task.id}`,
      target_id: task.id,
      kind: "decision",
      run_id: task.run_id,
      title: candidate.recommendation || candidate.decision || parameters.recommendation || task.task_type,
      summary: candidate.tradeoff || candidate.rationale || task.reasoning,
      status: result.decision_status || workStatus(status),
      evidence: result.child_requirement_ids || result.child_task_ids || [],
      created_at: task.created_at,
      payload: result,
    });
  } else if (capability === "product_requirement_issue" || capability === "create_product_requirement_issue") {
    add({
      id: `requirement:${task.id}`,
      target_id: task.id,
      kind: "requirement",
      run_id: task.run_id,
      title: parameters.requirement || task.task_type || "Build requirement",
      summary: parameters.expected_outcome || parameters.strategic_rationale || result.summary,
      status: result.state || workStatus(status),
      evidence: result.delivery_evidence || result.delivery_artifacts || [],
      created_at: task.created_at,
      payload: { ...parameters, ...result },
    });
  } else if (capability === "github_change_request") {
    const filesChanged = Array.isArray(result.files_changed)
      ? result.files_changed.length
      : Number(result.files_changed) || 0;
    add({
      id: `github_change:${task.id}`,
      target_id: task.id,
      kind: "github_change",
      run_id: task.run_id,
      title: result.summary || task.task_type || "GitHub change request",
      summary: result.repository ? `${result.repository}${filesChanged ? ` · ${filesChanged} file${filesChanged === 1 ? "" : "s"} changed` : ""}` : task.reasoning,
      status: result.state || workStatus(status),
      evidence: result.pull_request_url ? [{ type: "pull_request", url: result.pull_request_url }] : [],
      created_at: task.created_at,
      payload: result,
    });
  } else if (capability === "connector_write") {
    const connectorResult = safeObject(result.result);
    add({
      id: `connector:${task.id}`,
      target_id: connectorResult.url || connectorResult.id || task.id,
      kind: "connector_record",
      run_id: task.run_id,
      title: task.task_type || `${result.connector || parameters.connector || "Connected tool"} update`,
      summary: connectorResult.summary || result.tool || parameters.tool || task.reasoning,
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  } else if (capability === "consult_agents") {
    const specialistResults = Array.isArray(result.results)
      ? result.results
      : Object.values(safeObject(result.results));
    add({
      id: `analysis:${task.id}`,
      kind: "analysis",
      run_id: task.run_id,
      title: task.task_type || "Pabs analysis",
      summary: `${specialistResults.length} specialist result${specialistResults.length === 1 ? "" : "s"}.`,
      status: workStatus(status),
      created_at: task.completed_at,
      payload: { ...result, specialistResults },
    });
  } else if (capability === "refresh_recommended_next_steps") {
    const recommendationCount = Number(result.newNextStepCount) || 0;
    const preservedBlockerCount = Number(result.preservedBlockerCount) || 0;
    const summaryParts = [
      `${recommendationCount} recommended next step${recommendationCount === 1 ? "" : "s"} refreshed`,
    ];
    if (preservedBlockerCount) {
      summaryParts.push(`${preservedBlockerCount} blocker${preservedBlockerCount === 1 ? "" : "s"} preserved`);
    }
    add({
      id: `recommendations:${result.reportId || task.id}`,
      kind: "recommendation_set",
      run_id: task.run_id,
      title: "Recommended next steps",
      summary: `${summaryParts.join(" · ")}.`,
      outcome: result.verdictCall
        ? { type: result.verdictCall, label: readableOutcome(result.verdictCall) }
        : null,
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  } else if (capability === "enable_feature") {
    add({
      id: `configuration:${task.id}`,
      kind: "configuration_change",
      run_id: task.run_id,
      title: task.task_type || "Pabs feature enabled",
      summary: result.summary || task.reasoning,
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  } else if (
    String(capability).startsWith("update_")
    || (result.reactField && Object.prototype.hasOwnProperty.call(result, "rawValue"))
  ) {
    const field = result.reactField || String(capability).replace(/^update_/, "");
    const label = humanizeIdentifier(field);
    const value = planChangeValue({ capability, result, parameters });
    const itemCount = Array.isArray(value) ? value.length : null;
    const resultDescription = Object.prototype.hasOwnProperty.call(result, "rawValue")
      && typeof result.value === "string"
      && !new RegExp(`^${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} updated\\.?$`, "i").test(result.value.trim())
      ? result.value.trim()
      : null;
    add({
      id: `plan_change:${task.id}`,
      kind: "plan_change",
      run_id: task.run_id,
      title: label || task.task_type || "Plan change",
      summary: resultDescription || (
        itemCount == null
          ? null
          : `${itemCount} ${label.toLowerCase()} saved.`
      ),
      status: workStatus(status),
      created_at: task.completed_at,
      payload: {
        ...result,
        change: {
          field,
          label,
          value,
        },
      },
    });
  } else if (!items.length && Object.keys(result).length && !String(task?.task_type || "").startsWith("MCP ")) {
    add({
      id: `result:${task.id}`,
      kind: "text_result",
      run_id: task.run_id,
      title: task.task_type || "Pabs result",
      summary: communicationText(resultCommunication(result), result.message || result.summary || result.value || task.reasoning),
      status: workStatus(status),
      created_at: task.completed_at,
      payload: result,
    });
  }

  // Artifacts can coexist with the authored update (for example a journey
  // audit delivered as a document). A document must not consume that update.
  const communication = resultCommunication(result);
  if (communication && !items.some(item => NARRATIVE_WORK_KINDS.has(item.kind) && item.communication)) {
    add({ id: `communication:${task.id}`, kind: 'text_result', run_id: task.run_id,
      title: 'Pabs update', communication, summary: communicationText(communication),
      status: workStatus(status), created_at: task.completed_at, payload: {} });
  }
  return items;
}

// `task.error_message` is whatever the runner caught when the job threw. It is an
// internal diagnostic (SQL errors, driver failures, stack traces), never authored for
// a founder to read, so it must not reach the UI verbatim. Classify it instead and
// return language that explains what happened and what the founder can do next. An
// authored `result.message` always wins, because that one was written to be read.
const FAILURE_CLASSES = [
  {
    code: "data_schema",
    match: /column .* does not exist|relation .* does not exist|syntax error at or near|duplicate key value|violates .* constraint|null value in column|invalid input syntax/i,
    message: "Pabs hit a problem in its own data setup and could not finish this job. Retrying may work. If it keeps failing, this needs a fix from the Pabs team.",
  },
  {
    code: "connection",
    match: /ECONNREFUSED|ENOTFOUND|ECONNRESET|EAI_AGAIN|socket hang up|fetch failed|network error|getaddrinfo/i,
    message: "Pabs could not reach a service this job depends on. Retry in a moment.",
  },
  {
    code: "timeout",
    match: /ETIMEDOUT|timed? ?out|deadline exceeded/i,
    message: "This job took too long to finish and Pabs stopped it. Retry when you are ready.",
  },
  {
    code: "permission",
    match: /unauthorized|forbidden|invalid token|permission denied|401|403/i,
    message: "Pabs was not allowed to access something this job needs. Check the connected account, then retry.",
  },
  {
    code: "runtime",
    match: /TypeError|ReferenceError|is not a function|Cannot read propert|undefined is not|at .*\.js:\d+/i,
    message: "Pabs could not complete this job because of an internal error. Retry, and let the Pabs team know if it keeps failing.",
  },
];

function classifyFailure(rawMessage) {
  const text = String(rawMessage || "").trim();
  if (!text) return { code: "unknown", message: null };
  const matched = FAILURE_CLASSES.find(entry => entry.match.test(text));
  return matched
    ? { code: matched.code, message: matched.message }
    : { code: "unknown", message: null };
}

function userSafeFailureMessage(task, result = {}) {
  if (String(result.message || "").trim()) return result.message;
  return classifyFailure(task?.error_message).message
    || "Pabs could not complete this job. Retry, and let the Pabs team know if it keeps failing.";
}

function projectActivity({ task, runs = [], runSteps = [], outputs = [], events = [], interactions = [], mcpActivity = [] }) {
  const state = presentationState(task?.status);
  // A brand-new proposal has no activity. A rescheduled durable mission still
  // owns its prior execution history, so moving it back to Planned must not
  // make its audit trail disappear.
  if (state === "proposal") return [];
  if (state === "scheduled" && !events.length && !interactions.length) return [];

  const explicitRunId = task?.run_id || null;
  const relevantRun = explicitRunId
    ? runs.find(run => Number(run.id) === Number(explicitRunId))
    : runs.find(run => run.status === task?.status)
      || runs.find(run => ["running", "waiting_for_user", "waiting_for_approval", "waiting_for_evidence", "waiting_for_credits", "blocked"].includes(run.status))
      || runs[0]
      || null;
  const relevantRunId = state === "scheduled" && !explicitRunId
    ? null
    : explicitRunId || relevantRun?.id || null;
  const relevantSteps = relevantRunId
    ? runSteps.filter(step => Number(step.run_id) === Number(relevantRunId))
    : runSteps;
  const relevantOutputs = relevantRunId
    ? outputs.filter(output => Number(output.run_id) === Number(relevantRunId))
    : outputs;
  const relevantInteractions = relevantRunId
    ? interactions.filter(interaction => Number(interaction.run_id) === Number(relevantRunId))
    : interactions;

  const activity = [];
  const add = item => activity.push(item);
  const mcpLabel = (toolName, client) => ({
    get_design_authority: `${client} checked Pabs’s design direction`,
    prepare_design_brief: `${client} prepared the work with Pabs`,
    review_design_promotion: `${client} asked Pabs for the Design Review`,
    audit_user_journey: `${client} supplied journey evidence to Pabs`,
    generate_design_remediation_plan: `${client} asked Pabs to prepare the correction brief`,
  }[toolName] || `${client} continued this work with Pabs`);
  for (const call of mcpActivity) {
    const parameters = safeObject(call.parameters);
    const result = safeObject(call.result);
    const isMcpCall = String(call.task_type || "").startsWith("MCP") || Boolean(parameters.tool_name);
    if (!isMcpCall) {
      const childLabel = {
        generate_design_remediation_plan: "Pabs created the correction brief",
        audit_user_journey: "Pabs created a linked journey audit",
        product_requirement_issue: "Pabs created the delivery requirement",
      }[call.capability] || `Pabs created ${call.task_type || "linked work"}`;
      add({
        id: `child:${call.id}`,
        type: "child_job",
        label: childLabel,
        status: call.status,
        at: call.created_at,
        child_task_id: call.id,
      });
      continue;
    }
    add({
      id: `mcp:${call.id}`,
      type: call.status === "failed" ? "tool_failed" : "tool_completed",
      label: mcpLabel(parameters.tool_name, parameters.client || "AI builder"),
      status: call.status,
      at: call.completed_at || call.created_at,
      tool_name: parameters.tool_name || null,
      client: parameters.client || null,
      credits: result.credit_usage?.deduction?.creditsDeducted ?? null,
    });
  }
  for (const step of relevantSteps) {
    add({
      id: `step:${step.id}`,
      type: `step_${step.status}`,
      label: step.name || step.tool_name || "Mission step",
      status: step.status,
      at: step.completed_at || step.started_at || step.updated_at || step.created_at,
      run_id: step.run_id,
    });
  }
  for (const output of relevantOutputs) {
    if (!["chat_delivery", "email_notification", "email_deliverable"].includes(output.output_kind)) continue;
    add({
      id: `delivery:${output.id}`,
      type: "delivery",
      label: output.output_kind === "chat_delivery" ? "Sent to plan chat" : output.title || "Email delivered",
      status: output.status,
      at: output.created_at,
      delivery_ref: output.delivery_ref,
    });
  }
  for (const interaction of relevantInteractions) {
    add({
      id: `interaction:${interaction.id}`,
      type: interaction.status === "open" ? "waiting" : "response_received",
      label: interaction.status === "open" ? "Waiting for your response" : "Response received",
      status: interaction.status,
      at: interaction.resolved_at || interaction.requested_at || interaction.created_at,
    });
  }
  // A pause/block entry that just says "task: blocked" tells the user nothing —
  // the actual explanation (stalled, out of credits, hit the safety ceiling, or
  // a genuine question) lives in the run's own result, not the generic lifecycle
  // transition label. Only the transition that produced the CURRENT status can
  // be enriched this way — task.result reflects the latest state, not history.
  const currentResult = safeObject(task?.result);
  const continuation = safeObject(currentResult.continuation);
  if (continuation.recorded_at) {
    add({ id: `decision:${task.id}:${continuation.recorded_at}`, type: "decision_recorded",
      label: continuation.message || "Decision recorded", status: "completed",
      at: continuation.recorded_at, child_task_id: continuation.task_id || null });
  }
  for (const event of events) {
    // A canonical mission transition writes task, mission, and run events in the
    // same transaction. They are three audit records but one user-visible moment.
    if (event.entity_type && event.entity_type !== "task") continue;
    const isCurrentTransition = event.to_status === task?.status;
    const reasonSummary = isCurrentTransition && ["blocked", "waiting_for_credits"].includes(event.to_status)
      ? (currentResult.summary || currentResult.blocked_reason || currentResult.message || null)
      : null;
    add({
      id: `event:${event.id}`,
      type: "state_change",
      label: reasonSummary
        ? `${String(event.entity_type || "job").replace(/_/g, " ")}: ${reasonSummary}`
        : `${String(event.entity_type || "job").replace(/_/g, " ")}: ${String(event.to_status || "").replace(/_/g, " ")}`,
      status: event.to_status,
      at: event.created_at,
      reason: event.reason,
    });
  }
  if (task?.completed_at && !activity.some(item => item.type === "state_change" && item.status === "completed")) {
    add({ id: `task-completed:${task.id}`, type: "completed", label: "Job completed", at: task.completed_at });
  }
  if (task?.status === "failed" && task.updated_at) {
    add({ id: `task-failed:${task.id}`, type: "failed", label: "Job failed", at: task.updated_at, reason: userSafeFailureMessage(task, safeObject(task.result)) });
  }
  // Generic lifecycle markers are useful when they are the only history. When
  // concrete steps exist they add technical noise and can appear after the work
  // for child delivery tasks whose record was created late in the parent run.
  if (!activity.length) {
    if (task?.created_at) add({ id: `task-created:${task.id}`, type: "created", label: "Job created", at: task.created_at });
    if (task?.started_at) add({ id: `task-started:${task.id}`, type: "started", label: "Job started", at: task.started_at });
  }
  return activity
    .filter(item => item.at)
    .sort((a, b) => new Date(b.at) - new Date(a.at));
}

function projectRequest(task = {}) {
  const parameters = safeObject(task.parameters);
  const result = safeObject(task.result);
  // Resuming a durable mission updates task.reasoning with the continuation
  // instruction while preserving task.created_at. That mutable field is not an
  // opening-request event; the resolved interaction is its source of truth.
  const isInteractionContinuation = Boolean(parameters.interaction_id || parameters.continuation);
  const message = parameters.intended_decision
    || parameters.request
    || parameters.objective
    || result.original_question
    || parameters.original_question
    || (!isInteractionContinuation ? task.reasoning : null)
    || null;
  if (!message) return null;
  const namedRequester = String(message).match(/^([^,(]+?)(?:\s*\([^)]*\))?\s+(?:wants|asked)\b/i)?.[1]?.trim();
  const createdBy = String(task.created_by || "");
  const isPabsCreated = ["design_authority", "mission_agent", "chat_design_lead", "pabs"].includes(createdBy);
  const requestedBy = parameters.requested_by
    || parameters.requester_name
    || parameters.builder_name
    || namedRequester
    || (createdBy.startsWith("mcp") ? "AI builder" : isPabsCreated ? "Pabs" : "Plan owner");
  const actor = String(requestedBy).trim().toLowerCase() === "pabs"
    || isPabsCreated
    ? "pabs"
    : "requester";
  return {
    message,
    requested_by: requestedBy,
    actor,
    via: parameters.builder_client || (createdBy.startsWith("mcp") ? "MCP" : null),
    at: task.created_at || null,
  };
}

function projectObjective(task = {}, mission = null) {
  const parameters = safeObject(task.parameters);
  const result = safeObject(task.result);
  return mission?.objective
    || parameters.objective
    || parameters.mission_objective
    || parameters.intended_decision
    || result.original_question
    || parameters.original_question
    || task.reasoning
    || null;
}

function projectNow({ task, mission, openInteraction, work }) {
  const result = safeObject(task?.result);
  const rawStatus = task?.status || mission?.status || "todo";
  const state = presentationState(rawStatus);
  const reason = actionReason(rawStatus);
  const needsUser = openInteraction?.prompt
    || result.needs_user
    || result.needs_user_input
    || result.user_prompt
    || result.question
    || null;
  const relatedWorkIds = work.map(item => item.id);
  const base = {
    state,
    reason,
    raw_status: rawStatus,
    display_mode: ["completed", "cancelled"].includes(state)
      ? "omit"
      : ["needs_action", "blocked", "waiting_for_credits", "failed"].includes(state)
        ? "callout"
        : "summary",
    title: null,
    message: null,
    related_work_ids: relatedWorkIds,
    primary_action: null,
    secondary_actions: [],
  };
  if (rawStatus === "waiting_for_evidence" &&
      safeObject(result.work_protocol).current_actor === "external_builder") {
    return { ...base, display_mode: "omit", title: linkedWorkStatus(task),
      message: result.next_action || null, primary_action: null, secondary_actions: [] };
  }
  // Design remediation owns its confirmation inside the correction brief.
  // Suppress the generic needs-action callout so the Job never offers a
  // competing reply-in-chat path for the same decision.
  if (task?.capability === "generate_design_remediation_plan" && work.some(item => item.kind === "design_remediation")) {
    return { ...base, display_mode: "omit", title: null, message: null, primary_action: null, secondary_actions: [] };
  }
  if (task?.capability === "generate_design_exception_review" && work.some(item => item.kind === "design_exception")) {
    return { ...base, title: "Decide this design exception", message: "Review the bounded risk, conditions, owner, and reassessment point before granting or denying the exception." };
  }
  // Evidence requested from an external MCP builder is operational work, not a
  // request for the Pabs user. Keep the Job visible as in progress without the
  // generic "Review Pabs's work / Reply in chat" intervention card.
  if (
    task?.capability === "review_design_promotion"
    && rawStatus === "waiting_for_evidence"
    && safeObject(result.review).evidence_status === "supplement_required"
    && safeObject(safeObject(result.review).evidence_request).human_intervention_required !== true
  ) {
    return {
      ...base,
      state: "running",
      reason: "external_builder_evidence",
      display_mode: "omit",
      title: null,
      message: null,
      primary_action: null,
      secondary_actions: [],
    };
  }
  // A pabs_prepares_handoff mission's objective is often written as one merged
  // sentence ("recruit solo founders... run them through... report this
  // cohort") — accurate about the outcome, but silent on who does which part.
  // The recommendation that created it already separates this (pabs_support
  // .output vs .human_next); surface that instead of letting the merged
  // objective read as "Pabs will do all of this".
  const humanNext = mission?.scope?.human_next || null;
  if (state === "proposal") {
    const objective = mission?.scope?.pabs_deliverable
      || mission?.objective
      || safeObject(task?.parameters).objective
      || task.reasoning
      || "Review this suggested job and decide whether Pabs should schedule it.";
    return { ...base, title: "What Pabs will do", message: objective, human_next: humanNext, primary_action: { type: "activate", label: "Schedule now" } };
  }
  if (state === "scheduled") {
    const objective = mission?.scope?.pabs_deliverable
      || mission?.objective
      || safeObject(task?.parameters).objective
      || task.reasoning
      || "Pabs will run this job when scheduled.";
    return {
      ...base,
      title: "What Pabs will do",
      message: objective,
      human_next: humanNext,
      schedule_at: mission?.next_run_at || task.scheduled_at || null,
      primary_action: rawStatus === "paused" ? { type: "resume", label: "Resume" } : { type: "run_now", label: "Run now" },
    };
  }
  if (state === "running") return { ...base, title: "Pabs is working", message: task.current_step ? `Currently running ${String(task.current_step).replace(/_/g, " ")}` : "This job is in progress.", secondary_actions: [{ type: "pause", label: "Pause" }] };
  if (state === "needs_action") {
    const titles = { approval: "Review Pabs’s work", input: "Pabs needs your input", evidence: "Add implementation evidence" };
    const title = relatedWorkIds.length ? "Review Pabs’s work" : (titles[reason] || "Pabs needs your input");
    const message = needsUser || "Review this job and tell Pabs how to continue.";
    const responseOptions = Array.isArray(result.needs_user_options)
      ? result.needs_user_options.filter(option => typeof option === "string" && option.trim())
      : [];
    // A model-provided confirm question gets real buttons matching its actual
    // answers, always with a genuine way to decline (see MissionRunner.js's
    // FINAL RESPONSE FORMAT). Anything else — a genuinely open question, or
    // an older result stored before this field existed — falls back to a
    // plain chat reply. That fallback used to carry labels like "Review" or
    // "Add evidence" implying a distinct flow that didn't actually exist;
    // every case funneled through the same "open chat and type" mechanism,
    // so the label should say that honestly instead of promising more.
    if (result.needs_user_response_type === "confirm" && responseOptions.length >= 2) {
      return { ...base, title, message, primary_action: { type: "confirm_choice", options: responseOptions }, secondary_actions: [] };
    }
    return { ...base, title, message, primary_action: { type: "reply_in_chat", label: "Reply in chat" }, secondary_actions: [] };
  }
  // result.message (when present) is the run's own account of what happened before it
  // blocked — needsUser is only the trailing question. Showing needsUser alone drops
  // the explanation and leaves the user with just "should I continue?" and no why.
  if (state === "blocked") return { ...base, title: "This job is blocked", message: result.message || result.blocked_reason || needsUser || task.error_message || "Pabs needs help to continue.", primary_action: { type: "resolve_blocker", label: "Continue", loading_label: "Continuing job…" }, secondary_actions: [{ type: "pause", label: "Pause future runs" }] };
  if (state === "waiting_for_credits") return { ...base, title: "Needs more credits to continue", message: result.message || needsUser || "This mission is ready to continue once your plan has enough credits.", primary_action: { type: "resolve_blocker", label: "I've added credits, continue", loading_label: "Continuing job…" }, secondary_actions: [{ type: "pause", label: "Pause future runs" }] };
  if (state === "completed") return { ...base, title: "Completed", message: communicationText(resultCommunication(result), result.message || result.summary || "I completed this job."), primary_action: work[0]?.primary_action || null };
  if (state === "failed") return { ...base, title: "Something went wrong", message: userSafeFailureMessage(task, result), primary_action: { type: "retry", label: "Retry" } };
  return { ...base, title: "Cancelled", message: "This job is no longer active." };
}

const DURABLE_WORK_KINDS = new Set([
  "document",
  "requirement",
  "email",
  "pulse",
  "decision",
  "github_change",
  "connector_record",
  "plan_change",
  "configuration_change",
]);

const NARRATIVE_WORK_KINDS = new Set([
  "text_result",
  "mission_result",
  "analysis",
  "recommendation_set",
  "design_brief",
  "design_authority_review",
  "design_remediation",
  "design_exception",
  "accessibility_assessment",
  "design_implementation_comparison",
  "design_system_usage_review",
]);

const SELF_CONTAINED_WORK_KINDS = new Set([
  "mission_result",
  "design_authority_review",
  "design_remediation",
  "design_exception",
  "accessibility_assessment",
  "design_implementation_comparison",
  "design_system_usage_review",
]);

// The story contract is deliberately independent of React. It gives every job
// one presentation grammar: chronological actor turns, widgets belonging to a
// turn, durable artifacts, exceptional state, and finally the audit trail.
// Specialist renderers can enrich a turn without deciding where it belongs.
function interactionResponseMessage(interaction = {}) {
  const response = safeObject(interaction.response_payload);
  return presentationText(
    response.message || response.response || response.answer || response.text || response.value || response.instruction,
    ["message", "response", "answer", "text", "value", "instruction"]
  );
}

// A handover tells the same story as other jobs, from its own records: the
// owner's request, Pabs's reply with what is already done and the prompt to
// give the AI tool, the pickup, each return in the AI tool's words, Pabs's
// assessment of it, and why it was closed. The request written for the AI tool
// is not a message in this story; it stays in the full detail.
function assessmentMessage(learning) {
  if (!learning) return null;
  if (learning.evaluation_status === "failed") return "I couldn’t assess this return yet. Open the Learning to try again.";
  if (learning.evaluation_status !== "evaluated") return "I’m assessing what came back.";
  const communication = safeObject(learning.quality_metadata).communication;
  const messages = Array.isArray(communication?.messages) ? communication.messages.filter(item => typeof item === "string" && item.trim()) : [];
  return messages.length ? messages.join("\n\n") : presentationText(learning.strategic_reasoning) || null;
}
function projectHandoverStory(task, assessments = []) {
  const result = safeObject(task?.result);
  const isStep = result.origin?.type === "next_step";
  const subject = result.next_step || result.blocker || task?.task_type || "this work";
  const progress = safeObject(safeObject(result.request?.context).progress);
  const client = result.claim?.client || "Your AI tool";
  const waiting = result.state === "ready_for_ai_tool" && !result.claim;
  const turns = [{
    id: `handover-request:${task?.id}`, kind: "message", actor: "requester", actor_label: "Plan owner",
    at: task?.created_at || null, message: `Hand this ${isStep ? "next step" : "blocker"} to my AI tool: ${subject}`, work_ids: [],
  }, {
    id: `handover-ready:${task?.id}`, kind: "message", actor: "pabs", actor_label: "Pabs", at: task?.created_at || null,
    title: waiting ? "Ready for your AI tool" : "Handed over to your AI tool",
    message: progress.remaining
      ? `It continues from the work already returned instead of starting again. Still needed: ${progress.remaining}`
      : "I gave it this plan’s context. It does the work, then returns what changed, how it checked it and what remains.",
    primary_action: waiting ? { type: "copy_ai_prompt", label: "In your connected AI tool, ask:", prompt: `Check Pabs for the work I handed over (Job #${task?.id}).` } : null,
    work_ids: [],
  }];
  if (result.claim?.at) {
    turns.push({ id: `handover-claim:${task?.id}`, kind: "message", actor: "pabs", actor_label: "Pabs", at: result.claim.at, message: `${client} picked this up.`, work_ids: [] });
  }
  for (const entry of Array.isArray(result.returns) ? result.returns : []) {
    const returnedText = [entry.summary, entry.remaining ? `Still to do:\n${entry.remaining}` : null].filter(Boolean).join("\n\n");
    turns.push({ id: `handover-return:${task?.id}:${entry.id}`, kind: "message", actor: "ai_tool", actor_label: client, at: entry.at || null, message: returnedText || null, work_ids: [] });
    const learning = assessments.find(item => Number(item.id) === Number(entry.learning_id));
    const message = assessmentMessage(learning);
    if (message || entry.learning_id) {
      turns.push({
        id: `handover-assessment:${task?.id}:${entry.id}`, kind: "message", actor: "pabs", actor_label: "Pabs", at: entry.at || null,
        message: message || "This return is saved as a Learning.",
        primary_action: entry.learning_id ? { type: "open_learning", label: `Open Learning #${entry.learning_id}`, learning_id: Number(entry.learning_id) } : null,
        work_ids: [],
      });
    }
  }
  if (task?.status === "cancelled" && result.closed_reason) {
    turns.push({ id: `handover-closed:${task?.id}`, kind: "state", actor: "pabs", actor_label: "Pabs", at: task?.updated_at || null, state: "cancelled", title: "Closed", message: result.closed_reason, work_ids: [] });
  }
  // The handover's own actions belong to Pabs's latest message, as in other jobs.
  const latestPabsMessage = [...turns].reverse().find(turn => turn.actor === "pabs" && turn.kind === "message");
  if (latestPabsMessage && result.origin?.id) {
    latestPabsMessage.secondary_actions = [
      { type: "open_origin", label: isStep ? "View next step" : "View blocker", origin: result.origin },
      ...(waiting ? [{ type: "change_handover", label: "Ask Pabs to change this" }] : []),
    ];
  }
  return { version: 1, turns, artifacts: [], state: null };
}

function projectStory({ task, request, now, work = [], runs = [], interactions = [], assessments = [] }) {
  if (task?.capability === 'external_blocker_work') return projectHandoverStory(task, assessments);
  const turns = [];
  if (request?.message) {
    turns.push({
      id: `request:${task?.id || "job"}`,
      kind: "message",
      actor: request.actor || "requester",
      actor_label: request.requested_by || "Plan owner",
      via: request.via || null,
      at: request.at || null,
      message: request.message,
      work_ids: [],
    });
  }

  const narrativeWork = work.filter(item => NARRATIVE_WORK_KINDS.has(item.kind));
  const selfContainedRunIds = new Set(narrativeWork
    .filter(item => SELF_CONTAINED_WORK_KINDS.has(item.kind))
    .map(item => item.run_id || "unscoped"));
  const runsById = new Map(runs.map(run => [run.id, run]));
  const workByRun = new Map();
  // Every output belongs to the run which produced it. Durable artifacts are
  // referenced by the turn but remain unique work records, so a document or
  // requirement is never rendered a second time merely because a run resumed.
  for (const item of work) {
    const key = item.run_id || "unscoped";
    if (!workByRun.has(key)) workByRun.set(key, []);
    workByRun.get(key).push(item);
  }

  for (const interaction of interactions) {
    const requestedAt = interaction.requested_at || interaction.created_at || null;
    // A mission result already owns its needs-user prompt. Once that prompt is
    // resolved, preserve the owner's response as the next turn without
    // repeating Pabs's prompt as a second speech bubble.
    const promptOwnedByWork = selfContainedRunIds.has(interaction.run_id || "unscoped");
    if (interaction.prompt && !promptOwnedByWork) {
      turns.push({
        id: `interaction-prompt:${interaction.id}`,
        kind: "message",
        actor: "pabs",
        actor_label: "Pabs",
        run_id: interaction.run_id || null,
        at: requestedAt,
        message: interaction.prompt,
        interaction_id: interaction.id,
        work_ids: [],
      });
    }
    const responseMessage = interactionResponseMessage(interaction);
    if (interaction.status === "resolved" && responseMessage) {
      turns.push({
        id: `interaction-response:${interaction.id}`,
        kind: "message",
        actor: "requester",
        actor_label: "Plan owner",
        run_id: interaction.run_id || null,
        at: interaction.resolved_at || interaction.updated_at || requestedAt,
        message: responseMessage,
        interaction_id: interaction.id,
        work_ids: [],
      });
      const responsePayload = safeObject(interaction.response_payload);
      const storedConfirmationMessage = presentationText(
        responsePayload.action_confirmation || responsePayload.confirmation_message,
        ["action_confirmation", "confirmation_message"]
      );
      const confirmationMessage = responsePayload.action === "reschedule"
        ? `I rescheduled ${task?.task_type || "this job"}. I’ll run the same mission then, with its existing history preserved.`
        : storedConfirmationMessage;
      if (confirmationMessage) {
        turns.push({
          id: `interaction-confirmation:${interaction.id}`,
          kind: "message",
          actor: "pabs",
          actor_label: "Pabs",
          run_id: interaction.run_id || null,
          at: responsePayload.confirmed_at || interaction.resolved_at || interaction.updated_at || requestedAt,
          message: confirmationMessage,
          interaction_id: interaction.id,
          scheduled_at: responsePayload.scheduled_at || null,
          work_ids: [],
        });
      }
    }
  }
  for (const [runId, runWork] of workByRun) {
    const run = runsById.get(runId);
    turns.push({
      id: `response:${task?.id || "job"}:${runId}`,
      kind: "message",
      actor: "pabs",
      actor_label: "Pabs",
      run_id: runId === "unscoped" ? null : runId,
      at: runWork.find(item => item.created_at)?.created_at
        || run?.completed_at || run?.updated_at || run?.created_at
        || task?.completed_at || task?.updated_at || null,
      message: null,
      work_ids: runWork.map(item => item.id),
    });
  }

  // A needs-action prompt is part of Pabs's conversational turn. Keep a
  // separate state callout only for exceptional operational conditions.
  const state = null;
  const workOwnsInteraction = narrativeWork.some(item => SELF_CONTAINED_WORK_KINDS.has(item.kind));
  const openPromptAlreadyProjected = interactions.some(interaction => interaction.status === "open" && interaction.prompt);
  const prompt = now?.state === "needs_action" && now?.display_mode !== "omit" && !workOwnsInteraction
    && !openPromptAlreadyProjected
    ? {
        id: `prompt:${task?.id || "job"}`,
        kind: "message",
        actor: "pabs",
        actor_label: "Pabs",
        at: task?.updated_at || null,
        title: now.title,
        message: now.message,
        primary_action: now.primary_action,
        work_ids: [],
      }
    : null;
  if (prompt) turns.push(prompt);

  // Progress and exceptional outcomes are events in the job's story, not a
  // banner that visually predates the work which caused them.
  if (["running", "blocked", "waiting_for_credits", "failed", "completed", "cancelled"].includes(now?.state)) {
    const stateTurn = {
      id: `state:${task?.id || "job"}:${now.state}`,
      kind: "state",
      actor: "pabs",
      actor_label: "Pabs",
      at: task?.updated_at || task?.completed_at || null,
      title: now.title,
      message: now.message,
      state: now.state,
      primary_action: now.primary_action,
      work_ids: [],
    };
    const latestWorkTurn = [...turns].reverse().find(turn => (
      turn.actor === "pabs"
      && turn.work_ids?.length
      && (!task?.run_id || Number(turn.run_id) === Number(task.run_id))
    ));
    if (latestWorkTurn) {
      // The state is an outcome of this run, not another Pabs message. Keeping
      // it as a widget on the producing turn avoids repeating the run copy and
      // inventing an extra speech bubble/avatar in the timeline.
      latestWorkTurn.state_widget = {
        ...stateTurn,
        // The producing work already owns the narrative. The attached widget
        // communicates state and next action only, so lifecycle copy cannot be
        // repeated as a second message inside the same turn.
        message: null,
        supporting_text: safeObject(task?.result).next_trigger || null,
      };
    } else {
      turns.push(stateTurn);
    }
  }

  turns.sort((left, right) => {
    if (!left.at && !right.at) return 0;
    if (!left.at) return -1;
    if (!right.at) return 1;
    return new Date(left.at).getTime() - new Date(right.at).getTime();
  });

  // Actionability belongs to a chronological turn, never to a renderer or to
  // a mutable task result. A mission can retain many result snapshots, but at
  // most one of them can own the currently open interaction. Marking every
  // other result historical here prevents stale CTAs from reappearing in any
  // specialist Job renderer.
  const openInteraction = interactions.find(interaction => interaction.status === "open") || null;
  const resultTurns = turns.filter(turn => turn.actor === "pabs" && turn.work_ids?.length);
  const currentResultTurn = openInteraction
    ? [...resultTurns].reverse().find(turn => Number(turn.run_id) === Number(openInteraction.run_id))
    : now?.state === "needs_action"
      ? [...resultTurns].reverse().find(turn => (
          !task?.run_id || Number(turn.run_id) === Number(task.run_id)
        ))
      : null;
  for (const turn of resultTurns) {
    turn.action_status = turn === currentResultTurn ? "current" : "historical";
    turn.interaction_id = turn === currentResultTurn
      ? (openInteraction?.id || turn.interaction_id || null)
      : (turn.interaction_id || null);
  }

  return {
    version: 1,
    turns,
    artifacts: work.filter(item => DURABLE_WORK_KINDS.has(item.kind)),
    state,
  };
}

function buildPresentation({ task, mission = null, artifact = null, runs = [], runSteps = [], interactions = [], outputs = [], events = [], mcpActivity = [], linkedWork = [], handoverAssessments = [] }) {
  const handover = task?.capability === 'external_blocker_work';
  const work = handover ? [] : projectWork({ task, artifact, outputs, runSteps, interactions, runs });
  const openInteraction = interactions.find(item => item.status === "open") || null;
  const request = handover ? null : projectRequest(task) || (mission?.objective ? {
    message: mission.objective,
    requested_by: "Pabs",
    actor: "pabs",
    via: null,
    at: task?.created_at || null,
  } : null);
  const now = handover ? { state: task.status, raw_status: task.status, display_mode: 'omit', title: linkedWorkStatus(task), message: null, primary_action: null } : projectNow({ task, mission, openInteraction, work });
  return {
    version: 2,
    identity: {
      id: task?.id || null,
      title: mission?.name || task?.task_type || task?.capability || "Pabs job",
      kind: task?.capability || task?.task_type || "job",
    },
    now,
    response: jobResponse(task?.result),
    work,
    linked_work: linkedWork,
    activity: projectActivity({ task, runs, runSteps, outputs, events, interactions, mcpActivity }),
    details: {
      request,
      // A handover's objective is its AI-facing request, shown in full below.
      objective: handover ? null : projectObjective(task, mission),
      handover: handover ? {
        output: safeObject(safeObject(task?.result).request).output || null,
        completion: safeObject(safeObject(task?.result).request).completion || null,
        access: safeObject(safeObject(task?.result).request).access || null,
      } : null,
      schedule: {
        scheduled_at: task?.scheduled_at || null,
        next_run_at: mission?.next_run_at || null,
        trigger: mission?.trigger || null,
      },
      routine: mission?.trigger?.type === "recurring",
      notifications: {
        configurable: ["agent_mission", "generate_pulse"].includes(task?.capability),
        email_enabled: Boolean(mission?.authority?.email_plan_owner),
      },
      sources: Array.isArray(safeObject(task?.result).sources) ? safeObject(task?.result).sources : [],
      permissions: mission?.authority || null,
      credits: {
        deducted: safeObject(task?.parameters).credits_deducted ?? null,
        accounted_at: safeObject(task?.parameters).credit_accounted_at || null,
      },
      technical: {
        task_id: task?.id || null,
        mission_id: mission?.id || null,
        run_ids: runs.map(run => run.id),
        capability: task?.capability || null,
        attempt_count: task?.attempt_count || 0,
        // The classification, not the raw diagnostic: enough to triage a failure
        // without putting an internal error string in front of the founder.
        failure_code: task?.status === "failed" ? classifyFailure(task?.error_message).code : null,
      },
    },
    story: projectStory({ task, request, now, work, runs, interactions, assessments: handoverAssessments }),
  };
}

// A mission is a recurring container, not an execution history. Use recorded
// job ownership and explicit resume links so a terminal job cannot acquire
// work from unrelated executions of the same mission.
function selectJobRuns(task, runs = []) {
  if (task?.capability !== "agent_mission") return runs;
  const sameId = (left, right) => left != null && right != null && String(left) === String(right);
  const byId = new Map(runs.map(run => [String(run.id), run]));
  const selected = new Set();
  const include = run => {
    if (!run || selected.has(String(run.id))) return;
    selected.add(String(run.id));
    const metadata = safeObject(run.metadata);
    const previousId = run.resumed_from_run_id || metadata.resumed_from_run_id;
    if (previousId != null) include(byId.get(String(previousId)));
  };
  runs.forEach(run => {
    const metadata = safeObject(run.metadata);
    if (sameId(run.id, task.run_id)
      || sameId(run.task_id, task.id)
      || sameId(metadata.task_id, task.id)) include(run);
  });
  return runs.filter(run => selected.has(String(run.id)));
}

async function buildJobLifecycleProjection(db, {
  task,
  mission = null,
  artifact = null,
  runs = [],
  runSteps = [],
}) {
  runs = selectJobRuns(task, runs);
  const runIds = runs.map(run => run.id).filter(Boolean);
  const selectedRunIds = new Set(runIds.map(String));
  if (task?.capability === "agent_mission") {
    runSteps = runSteps.filter(step => selectedRunIds.has(String(step.run_id)));
  }
  // A handover's returns are assessed as Learnings; its story shows those assessments.
  const returnedLearningIds = task?.capability === "external_blocker_work"
    ? [...new Set((safeObject(task.result).returns || []).map(entry => Number(entry?.learning_id)).filter(id => Number.isSafeInteger(id) && id > 0))]
    : [];
  const [interactionsResult, outputsResult, eventsResult, mcpActivityResult, relatedTasksResult, handoverLearningsResult] = await Promise.all([
    runIds.length
      ? db.query(
          `SELECT * FROM agent_interactions
           WHERE run_id=ANY($1::int[])
           ORDER BY requested_at ASC, id ASC`,
          [runIds]
        )
      : { rows: [] },
    runIds.length
      ? db.query(
          `SELECT * FROM agent_run_outputs
           WHERE run_id=ANY($1::int[])
           ORDER BY created_at ASC, id ASC`,
          [runIds]
        )
      : { rows: [] },
    task?.id
      ? db.query(
          `SELECT * FROM agent_lifecycle_events
           WHERE task_id=$1
           ORDER BY created_at ASC, id ASC`,
          [task.id]
        )
      : { rows: [] },
    task?.id
      ? db.query(
          `SELECT id, task_type, capability, status, parameters, result, created_at, completed_at
           FROM agent_tasks
           WHERE plan_id=$1
             AND (
               (task_type LIKE 'MCP%' AND parameters->>'source_task_id'=$2)
               OR (
                 task_type NOT LIKE 'MCP%'
                 AND (parameters->>'source_task_id'=$2 OR result->>'source_task_id'=$2)
               )
             )
           ORDER BY created_at ASC, id ASC`,
          [task.plan_id, String(task.id)]
        )
      : { rows: [] },
    task?.id
      ? db.query(
          `SELECT id, task_type, capability, status, parameters, result, created_at, completed_at
           FROM agent_tasks
           WHERE plan_id=$1
             AND task_type NOT LIKE 'MCP%'
             AND status <> 'archived'
           ORDER BY created_at ASC, id ASC`,
          [task.plan_id]
        )
      : { rows: [] },
    returnedLearningIds.length
      ? db.query(
          `SELECT id, status, evaluation_status, quality_metadata, strategic_reasoning
           FROM plan_learnings WHERE plan_id=$1 AND id=ANY($2::int[])`,
          [task.plan_id, returnedLearningIds]
        )
      : { rows: [] },
  ]);

  const stepsByRun = new Map();
  for (const step of runSteps) {
    if (!stepsByRun.has(step.run_id)) stepsByRun.set(step.run_id, []);
    stepsByRun.get(step.run_id).push(step);
  }
  const interactionsByRun = new Map();
  for (const interaction of interactionsResult.rows) {
    if (!interactionsByRun.has(interaction.run_id)) interactionsByRun.set(interaction.run_id, []);
    interactionsByRun.get(interaction.run_id).push(interaction);
  }
  const outputsByRun = new Map();
  for (const output of outputsResult.rows) {
    if (!outputsByRun.has(output.run_id)) outputsByRun.set(output.run_id, []);
    outputsByRun.get(output.run_id).push(output);
  }

  const warnings = integrityWarnings(task, runSteps);
  const linkedWork = projectLinkedWork(task, relatedTasksResult.rows);
  return {
    canonical_status: task?.status || mission?.status || null,
    audit_quality: task?.audit_quality || (warnings.length ? "legacy_inconsistent" : "verified"),
    integrity_warnings: warnings,
    open_interaction: interactionsResult.rows.find(item => item.status === "open") || null,
    outputs: outputsResult.rows,
    lifecycle_events: eventsResult.rows,
    phases: runs.map(run => ({
      run,
      steps: stepsByRun.get(run.id) || [],
      interactions: interactionsByRun.get(run.id) || [],
      outputs: outputsByRun.get(run.id) || [],
    })),
    presentation: buildPresentation({
      task,
      mission,
      artifact,
      runs,
      runSteps,
      interactions: interactionsResult.rows,
      outputs: outputsResult.rows,
      events: eventsResult.rows,
      mcpActivity: mcpActivityResult.rows,
      linkedWork,
      handoverAssessments: handoverLearningsResult.rows,
    }),
  };
}

module.exports = {
  jobResponse,
  safeObject,
  governedTaskStatus,
  classifyFailure,
  userSafeFailureMessage,
  presentationText,
  readableOutcome,
  integrityWarnings,
  presentationState,
  projectWork,
  projectActivity,
  projectNow,
  projectStory,
  selectJobRuns,
  linkedWorkStatus,
  lifecycleStatusLabel,
  projectLinkedWork,
  projectRoutineLinkedWork,
  buildPresentation,
  buildJobLifecycleProjection,
};
